import { AddFocusEvent, Format } from './format.interface';
import { Card, Query } from '@deckplan.io/common';
import { CommanderData } from '@deckplan.io/common/decks';
import { Observable, of } from 'rxjs';
import { CardPickerComponent } from '../../cards/card-picker/card-picker.component';
import { filter, map } from 'rxjs/operators';

export class Brawl extends Format {

    private commander: Card;

    public addCardsQuery(): Query {
        const commanderData = this.deck.specialData as CommanderData;

        if (!commanderData || !commanderData.commanderId) {
            return null;
        }

        return {
            operator: 'and',
            operands: [
                {
                    dataField: {
                        title: 'Color Identity',
                        field: 'color_identity',
                        dataType: 'string'
                    },
                    operation: 'is in color identity',
                    value: commanderData.colorIdentity
                },
                {
                    operator: 'or',
                    operands: [
                        {
                            dataField: {
                                title: 'Name',
                                field: 'name',
                                dataType: 'string'
                            },
                            operation: 'singleton name',
                            value: this.deck.id
                        },
                        {
                            dataField: {
                                title: 'Type',
                                field: 'type_line',
                                dataType: 'string'
                            },
                            operation: 'startsWith',
                            value: 'Basic Land'
                        },
                        {
                            dataField: {
                                title: 'Text',
                                field: 'oracle_text',
                                dataType: 'string'
                            },
                            operation: 'includes',
                            value: 'any number of cards named'
                        }
                    ]
                },
                {
                    dataField: {
                        title: 'ID',
                        field: 'id',
                        dataType: 'string'
                    },
                    operation: '!=',
                    value: commanderData.commanderId
                },
                {
                    dataField: {
                        title: 'Legalities',
                        field: 'legalities',
                        dataType: 'string'
                    },
                    operation: 'includes',
                    value: `"brawl":"legal"`
                }
            ]
        } as any;
    }

    public addFocused(): Observable<AddFocusEvent> {
        const typeDataField = {title: 'Type', field: 'type', dataType: 'string'};
        const commanderQuery = {
            operator: 'and',
            operands: [
                {
                    dataField: {title: 'Legalities', field: 'legalities', dataType: 'string'},
                    operation: 'includes',
                    value: `"brawl":"legal"`
                },
                {
                    operator: 'or',
                    operands: [
                        {
                            dataField: typeDataField,
                            operation: 'includes',
                            value: 'Legendary%Creature'
                        },
                        {
                            dataField: typeDataField,
                            operation: 'includes',
                            value: 'Planeswalker'
                        }
                    ]
                }
            ]
        } as Query;
        const dialogRef = this.dialog.open(CardPickerComponent, {
            width: '100vw',
            minWidth: '100vw',
            height: '100vh',
            minHeight: '100vh',
            closeOnNavigation: false,
            disableClose: true,
            data: {
                query: commanderQuery,
                showOwnedCardsSelector: true,
                override: true
            }
        });
        return dialogRef.afterClosed().pipe(
            filter(card => card),
            map((card: any) => ({
                card,
                specialData: {
                    commanderId: card.id,
                    colorIdentity: card.colorIdentity
                } as CommanderData})
            ));
    }

    public deckCardsQuery(): Query {
        let query = null;
        const commanderData = this.deck.specialData as CommanderData;

        if (commanderData && commanderData.commanderId !== undefined) {
            query = {
                dataField: {field: 'id', dataType: 'string', title: 'ID'},
                operation: '!=',
                value: commanderData.commanderId
            };
        }

        return query;
    }

    public getFocused(): Observable<Card> {
        if (!this.deck.specialData) {
            return of(null);
        }

        const commanderData = this.deck.specialData as CommanderData;
        this.commander = this.deck.cards.find(card => card.id === commanderData.commanderId);

        if (commanderData && !this.commander && commanderData.commanderId) {
            return this.cardsService.getCard(commanderData.commanderId).pipe(map(card => this.commander = card));
        } else {
            return of(this.commander);
        }
    }

    public validate(): Card[] {
        if (!this.commander) {
            return [];
        }

        return this.deck.cards.filter(c => {
            if (c.legalities.brawl === 'legal') {
                return false;
            }

            for (const ci of c.colorIdentity) {
                if (this.commander.colorIdentity.find(i => i === ci)) {
                    return false;
                }
            }
            return true;
        });
    }

    public selectFocusText(): string {
        return 'Select Commander';
    }

}
